package com.hyperether.planner.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.hyperether.planner.model.Event
import com.hyperether.planner.model.Plan
import com.hyperether.planner.util.CircleWithInitial
import com.hyperether.planner.util.Dp
import com.hyperether.planner.util.Sp
import com.hyperether.planner.util.horizontalScrollbar
import com.hyperether.planner.util.verticalScrollbar
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.plus

@Composable
fun WeekPlanner(
    listOfPlans: MutableList<Plan>,
    celWidth: Int = 80,
    lastDay: Int = 8,
    spacer: Int = 5,
    weekStart: LocalDate,
    weekEnd: LocalDate,
    borderPathColor: Color = Color(231, 231, 231),
    namesColor: Color = Color(146, 144, 144),
    dateColor: Color = Color(83, 82, 82),
    namesIconsShown: Boolean = false,
    onEventClicked: (Event) -> Unit,
    onEventHovered: (Event?) -> Unit,
    maxWidth: Int? = null,
    isMobile: Boolean
) {
    println("WeekPlanner: listOfPlans:${listOfPlans.size}")
    val expandedNames = remember { mutableStateOf(!isMobile) }
    val celWidthInt = remember { mutableStateOf(celWidth) }
    val tableWidthDp = remember { mutableStateOf(Dp((7f * celWidthInt.value))) }
    maxWidth?.let {
        celWidthInt.value = remember(maxWidth) {
            val firstColumnWidth = if (namesIconsShown || expandedNames.value) (Dp(150f)) else Dp(
                50f
            )
            ((maxWidth - firstColumnWidth.value.toInt()) / 7).coerceAtLeast(40)
        }
        tableWidthDp.value = Dp(celWidthInt.value.toFloat() * 7)
    } ?: run {
        celWidthInt.value = celWidth
        tableWidthDp.value = Dp((7f * celWidthInt.value))
    }

    if (listOfPlans.size < 10) {
        while (listOfPlans.size < 11)
            listOfPlans.addAll(listOf(Plan("", mutableListOf())))
    }

    val horizontalScrollState = rememberScrollState()
    val verticalScrollState = rememberScrollState()

    Box(
        modifier = Modifier
            .fillMaxHeight()
            .verticalScrollbar(verticalScrollState)
            .horizontalScrollbar(horizontalScrollState)
            .pointerInput(Unit) {
                detectDragGestures { change, dragAmount ->
                    change.consume()
                    CoroutineScope(Dispatchers.Default).launch {
                        println("Y drag = ${dragAmount.y.toInt()}")
                        println("Y state = ${verticalScrollState.value}")
                        println("X drag = ${dragAmount.x.toInt()}")
                        println("X state = ${horizontalScrollState.value}")

                        verticalScrollState.scrollTo(verticalScrollState.value - dragAmount.y.toInt())

                        horizontalScrollState.scrollTo(horizontalScrollState.value - dragAmount.x.toInt())
                    }
                }
            }
            .background(Color.White)
            .border(Dp(1f), color = borderPathColor),
        contentAlignment = Alignment.TopStart
    ) {

        Row {
            //Names
            Column(
                modifier = Modifier.verticalScroll(verticalScrollState)
                    .background(Color.Transparent)
                    .border(Dp(1f), borderPathColor)
            ) {
                Box(
                    modifier = Modifier.size(height = Dp(50f), width = Dp(10f))
                        .background(Color.Transparent)
                )
                repeat(listOfPlans.size) { index ->
                    if (index == 0) {
                        Spacer(modifier = Modifier.height(Dp(12f)))
                    } else {
                        Spacer(modifier = Modifier.height(Dp(33f)))
                    }
                    Box(
                        modifier = Modifier.height(Dp(50f))
                            .width(
                                if (namesIconsShown || expandedNames.value) (Dp(150f)) else Dp(
                                    50f
                                )
                            )
                            .background(Color.Transparent),
                        contentAlignment = Alignment.CenterStart
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.padding(start = Dp(10f)).clickable {
                                expandedNames.value = !expandedNames.value
                            }) {
                            if (listOfPlans[index].person.isNotEmpty()) {
                                if (!isMobile) {
                                    CircleWithInitial(
                                        name = listOfPlans[index].person.uppercase(),
                                        modifier = Modifier.clickable {
                                            expandedNames.value = true
                                        },
                                        fontSize = 12
                                    )
                                    if (namesIconsShown) {
                                        Spacer(Modifier.width(Dp(8f)))
                                        Text(
                                            listOfPlans[index].person,
                                            textAlign = TextAlign.Center,
                                            color = namesColor,
                                            maxLines = 1,
                                            fontSize = Sp(14f),
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                } else {
                                    if (expandedNames.value) {
                                        Text(
                                            text = listOfPlans[index].person,
                                            textAlign = TextAlign.Center,
                                            color = namesColor,
                                            modifier = Modifier.clickable {
                                                expandedNames.value = false
                                            },
                                            fontSize = Sp(14f),
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    } else {
                                        CircleWithInitial(
                                            name = listOfPlans[index].person.uppercase(),
                                            modifier = Modifier.clickable {
                                                expandedNames.value = true
                                            },
                                            fontSize = 12
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            val pathEffect =
                PathEffect.dashPathEffect(floatArrayOf(5f, 5f), phase = 0f)

            Column(
                modifier = Modifier
                    .background(Color.Transparent)
                    .padding(end = 14.dp)
            ) {
                //days
                Row(
                    modifier = Modifier
                        .height(Dp(50f))
                        .horizontalScroll(horizontalScrollState),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    repeat(7) { index ->
                        Box(
                            modifier = Modifier
                                .width(Dp(celWidthInt.value.toFloat()))
                                .height(Dp(50f))
                                .drawBehind {
                                    drawLine(
                                        color = borderPathColor,
                                        start = Offset(x = 0f, y = size.height),
                                        end = Offset(x = size.width, y = size.height),
                                        strokeWidth = Dp(1f).toPx()
                                    )
                                    drawLine(
                                        color = borderPathColor,
                                        start = Offset(x = size.width, y = 0f),
                                        end = Offset(x = size.width, y = size.height),
                                        pathEffect = pathEffect,
                                        strokeWidth = Dp(1f).toPx()
                                    )
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                formatDateToDayMonth(weekStart.plus(DatePeriod(days = index))),
                                textAlign = TextAlign.Center,
                                color = dateColor,
                                fontSize = Sp(14f),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                }

                //content
                Column(
                    modifier = Modifier
                        .horizontalScroll(horizontalScrollState)
                        .verticalScroll(verticalScrollState)
                ) {
                    val textMeasurer = rememberTextMeasurer()
                    repeat(listOfPlans.size) { rowIndex ->
                        if (rowIndex == 0) {
                            Canvas(
                                modifier = Modifier
                                    .size(width = tableWidthDp.value, height = Dp(12f))
                            ) {
                                drawVerticalLines(
                                    Dp(celWidthInt.value.toFloat()),
                                    borderPathColor,
                                    pathEffect
                                )
                            }
                        } else {
                            Canvas(
                                modifier = Modifier
                                    .size(width = tableWidthDp.value, height = Dp(33f))
                            ) {
                                drawVerticalLines(
                                    Dp(celWidthInt.value.toFloat()),
                                    borderPathColor,
                                    pathEffect
                                )
                            }
                        }
                        val mapOfEvents = mutableMapOf<Int, MutableList<Event>>()

                        listOfPlans[rowIndex].events.forEach {
                            if (mapOfEvents[it.from.dayOfWeek.isoDayNumber] == null) {
                                mapOfEvents[it.from.dayOfWeek.isoDayNumber] = mutableListOf()
                            }

                            val dayEvents = mapOfEvents[it.from.dayOfWeek.isoDayNumber]
                            val hasEventWithSameJobId =
                                dayEvents?.any { event -> event.jobId == it.jobId } == true
                            if (!hasEventWithSameJobId) {
                                mapOfEvents[it.from.dayOfWeek.isoDayNumber]?.add(it)
                            }
                        }

                        if (mapOfEvents.isNotEmpty()) {
                            println("WeekPlanner: rowIndex:$rowIndex mapOfEvents:${mapOfEvents.size}")
                            Canvas(
                                modifier = Modifier.size(
                                    width = tableWidthDp.value,
                                    height = Dp(50f)
                                ).pointerInput(mapOfEvents) {
                                    awaitPointerEventScope {
                                        while (true) {
                                            val event = awaitPointerEvent()
                                            val offset = event.changes.firstOrNull()?.position
                                            if (offset == null) {
                                                println("WeekPlanner: Hovered over event NULL")
                                                onEventHovered(null)
                                                continue
                                            }

                                            // Handle tap gestures
                                            if (event.changes.any { it.pressed && it.previousPressed != it.pressed }) {
                                                // This is a tap/click
                                                isInArea(
                                                    mapOfEvents = mapOfEvents,
                                                    cellWidthPx = Dp(celWidthInt.value.toFloat()).toPx(),
                                                    cellHeightPx = Dp(50f).toPx(),
                                                    spacerPx = Dp(spacer.toFloat()).toPx(),
                                                    tableWidthPx = tableWidthDp.value.toPx(),
                                                    offset = offset
                                                ) {
                                                    it?.let { clickedEvent ->
                                                        println("WeekPlanner: Clicked on event: $clickedEvent")
                                                        onEventClicked(clickedEvent)
                                                    }
                                                }
                                            } else {
                                                // Handle hover detection
                                                var isThereHoveredEvent = false
//                                                println("WeekPlanner: isInArea mapOfEvents:${mapOfEvents.size}")
                                                isInArea(
                                                    mapOfEvents = mapOfEvents,
                                                    cellWidthPx = Dp(celWidthInt.value.toFloat()).toPx(),
                                                    cellHeightPx = Dp(50f).toPx(),
                                                    spacerPx = Dp(spacer.toFloat()).toPx(),
                                                    tableWidthPx = tableWidthDp.value.toPx(),
                                                    offset = offset
                                                ) {
                                                    println("WeekPlanner: Hovered over event $it")
                                                    onEventHovered(it)
                                                    isThereHoveredEvent = true
                                                }
                                                if (!isThereHoveredEvent) {
                                                    println("WeekPlanner: Hovered over event NULL")
                                                    onEventHovered(null)
                                                }
                                            }
                                        }
                                    }
                                }.pointerHoverIcon(PointerIcon.Hand)
                            ) {
                                //lines
                                for (i in 1..16) {
                                    drawLine(
                                        borderPathColor,
                                        start = Offset(
                                            Dp(celWidthInt.value.toFloat()).toPx() * i,
                                            0f
                                        ),
                                        end = Offset(
                                            Dp(celWidthInt.value.toFloat()).toPx() * i,
                                            Dp(50f).toPx()
                                        ),
                                        pathEffect = pathEffect,
                                        strokeWidth = Dp(1f).toPx()
                                    )
                                }

                                //plans
                                mapOfEvents.forEach {
                                    val numOfEvents = it.value.size
                                    var rectSpacingAdd = if (numOfEvents == 1) {
                                        2 * Dp(spacer.toFloat()).toPx()
                                    } else {
                                        2 * Dp(spacer.toFloat()).toPx() + Dp(spacer.toFloat()).toPx() / numOfEvents
                                    }
                                    val rectSize =
                                        Dp(celWidthInt.value.toFloat()).toPx() / numOfEvents - (numOfEvents + 1) * (Dp(
                                            spacer.toFloat()
                                        ).toPx() / numOfEvents)

                                    // counter starts from 0, so -1
                                    val startX =
                                        tableWidthDp.value.toPx() / 7 * (it.key - 1) + Dp(spacer.toFloat()).toPx()
                                    var topLeft = Offset(startX, Dp(0f).toPx())
                                    it.value.forEachIndexed { index, event ->
                                        drawRoundRect(
                                            color = event.color,
                                            topLeft = topLeft,
                                            size = Size(rectSize, Dp(50f).toPx()),
                                            cornerRadius = CornerRadius(10f, 10f)
                                        )

                                        topLeft = Offset(
                                            topLeft.x + rectSize + Dp(spacer.toFloat()).toPx(),
                                            Dp(0f).toPx()
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }


        //TODO make font size screen depended
//        Box(
//            modifier = Modifier.fillMaxSize()
//                .padding(horizontal =  Dp(20f), vertical = Dp(12f)),
//            contentAlignment = Alignment.BottomCenter
//        ) {
//            Row(
//                modifier = Modifier.shadow(elevation = 2Dp(), shape = RoundedCornerShape(Dp(10f)))
//                    .clip(RoundedCornerShape(Dp(10f)))
//                    .background(Color.White).padding(Dp(20f)),
//                verticalAlignment = Alignment.CenterVertically,
//                horizontalArrangement = Arrangement.SpaceBetween
//            ) {
//                Box(modifier = Modifier.size(Dp(20f)).clip(CircleShape).background(completedColor))
//                Text(
//                    "Completed",
//                    textAlign = TextAlign.Center,
//                    modifier = Modifier.padding(horizontal = Dp(12f)),
//                    fontSize = Sp(12f)
//                )
//
//                Box(modifier = Modifier.size(Dp(20f)).clip(CircleShape).background(pendingColor))
//                Text(
//                    "Pending",
//                    textAlign = TextAlign.Center,
//                    modifier = Modifier.padding(horizontal = Dp(12f)),
//                    fontSize = Sp(12f)
//                )
//
//                Box(modifier = Modifier.size(Dp(20f)).clip(CircleShape).background(delayedColor))
//                Text(
//                    "Delayed",
//                    textAlign = TextAlign.Center,
//                    modifier = Modifier.padding(horizontal = Dp(12f)),
//                    fontSize = Sp(12f)
//                )
//            }
//        }
    }
}

fun isInArea(
    mapOfEvents: Map<Int, MutableList<Event>>,
    cellWidthPx: Float,
    cellHeightPx: Float,
    spacerPx: Float,
    tableWidthPx: Float,
    offset: Offset,
    onEventDetected: (Event?) -> Unit
) {
    mapOfEvents.forEach { dayEvent ->
        dayEvent.value.forEachIndexed { index, event ->
            val numOfEvents = dayEvent.value.size
            val rectSize = cellWidthPx / numOfEvents - (numOfEvents + 1) * (spacerPx / numOfEvents)
            val startX = tableWidthPx / 7 * (dayEvent.key - 1) + spacerPx
            val rectTopLeft = Offset(
                startX + index * (rectSize + spacerPx), 0f
            )
            val rectBottomRight = Offset(
                rectTopLeft.x + rectSize,
                rectTopLeft.y + cellHeightPx
            )

            if (offset.x in rectTopLeft.x..rectBottomRight.x && offset.y in rectTopLeft.y..rectBottomRight.y) {
                // Perform action for the hovered event
                println("Hovered over event: $event startX:$startX rectTopLeft:$rectTopLeft rectSize:$rectSize")
                onEventDetected(event)
            }
        }
    }
}

fun formatDateToDayMonth(date: LocalDate): String {
    val day = date.dayOfMonth.toString().padStart(2, '0') // Ensure two digits for day
    val month =
        date.month.ordinal.plus(1).toString()
            .padStart(2, '0') // Ensure two digits for month
    return "$day/$month"
}

fun DrawScope.drawVerticalLines(
    celWidthDp: androidx.compose.ui.unit.Dp,
    borderColor: Color,
    pathEffect: PathEffect
) {
    for (i in 1..16) {
        drawLine(
            color = borderColor,
            start = Offset(celWidthDp.toPx() * i, 0f),
            end = Offset(celWidthDp.toPx() * i, size.height),
            pathEffect = pathEffect,
            strokeWidth = Dp(1f).toPx()
        )
    }
}